import { CalendarDays, Users, MapPin, ArrowRight } from "lucide-react";
import { useLenis } from "lenis/react";
import { Reveal } from "./Reveal";
import { GROUP_TRIPS } from "../../data/destinations";

export const GroupTravel = ({ onEnquire }) => {
  const lenis = useLenis();

  const reserve = (title) => {
    onEnquire(title);
    setTimeout(() => lenis?.scrollTo("#enquiry", { offset: -60, duration: 1.6 }), 60);
  };

  return (
    <section id="group-travel" data-testid="group-travel-section" className="bg-[#F7F0E3] pb-24 pt-14 sm:py-36">
      <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-accent text-xs font-bold uppercase tracking-[0.25em] text-[#E2725B]">
                Group Travel
              </p>
              <h2 className="mt-5 max-w-xl font-display text-3xl font-light leading-tight tracking-tight text-[#0D1F23] sm:text-5xl">
                Go <span className="italic text-[#E2725B]">together</span>
              </h2>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-[#4A5557] sm:text-base">
              Small groups, big tables, shared momentum. The departures we're crafting right
              now: bring your people, or come solo and leave with new ones.
            </p>
          </div>
        </Reveal>

        <div className="mt-16 grid gap-10 lg:grid-cols-2">
          {GROUP_TRIPS.map((trip, i) => (
            <Reveal key={trip.id} delay={i * 0.12}>
              <article
                data-testid={`group-trip-${trip.id}`}
                className="group flex h-full flex-col overflow-hidden rounded-3xl bg-white shadow-[0_20px_60px_-30px_rgba(13,31,35,0.25)]"
              >
                <div className={`relative overflow-hidden ${trip.images ? "flex h-64 gap-[3px] bg-[#0D1F23] sm:h-80" : trip.imageFit === "contain" ? "h-80 bg-[#0D1F23] sm:h-96" : "h-64 sm:h-72"}`}>
                  {trip.images ? (
                    trip.images.map((img) => (
                      <img
                        key={img.src}
                        src={img.src}
                        alt={trip.title}
                        loading="lazy"
                        style={{ objectPosition: img.pos }}
                        className="h-full w-1/3 object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-105"
                      />
                    ))
                  ) : (
                    <img
                      src={trip.image}
                      alt={trip.title}
                      loading="lazy"
                      style={trip.imagePos ? { objectPosition: trip.imagePos } : undefined}
                      className={`h-full w-full transition-transform duration-[1.6s] ease-out group-hover:scale-108 ${
                        trip.imageFit === "contain" ? "object-contain" : "object-cover"
                      }`}
                    />
                  )}
                  <span className="absolute left-5 top-5 rounded-full bg-[#0D1F23]/80 px-4 py-1.5 font-accent text-[10px] uppercase tracking-[0.25em] text-[#E2725B] backdrop-blur-sm">
                    {trip.label}
                  </span>
                  {trip.soldOut && (
                    <span
                      data-testid={`group-trip-${trip.id}-soldout`}
                      className="absolute right-5 top-5 rounded-full bg-[#E2725B] px-4 py-1.5 font-accent text-[10px] uppercase tracking-[0.25em] text-[#0D1F23]"
                    >
                      Sold Out
                    </span>
                  )}
                </div>

                <div className="flex flex-1 flex-col p-7 sm:p-9">
                  <h3 className="font-display text-3xl font-light tracking-tight text-[#0D1F23]">
                    {trip.title}
                  </h3>

                  <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
                    <span className="flex items-center gap-2 text-xs text-[#4A5557]">
                      <CalendarDays size={13} className="text-[#168A91]" /> {trip.dates}
                    </span>
                    <span className="flex items-center gap-2 text-xs text-[#4A5557]">
                      <Users size={13} className="text-[#168A91]" /> {trip.size}
                    </span>
                    <span className="flex items-center gap-2 text-xs text-[#4A5557]">
                      <MapPin size={13} className="text-[#168A91]" /> {trip.location}
                    </span>
                  </div>

                  <p className="mt-5 text-sm leading-relaxed text-[#4A5557] sm:text-base">
                    {trip.blurb}
                  </p>

                  <ul className="mt-5 space-y-2.5 border-t border-[#0D1F23]/10 pt-5">
                    {trip.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-3 font-display text-lg italic text-[#13272B]">
                        <span className="mt-2.5 h-1 w-4 shrink-0 rounded-full bg-[#E2725B]" />
                        {h}
                      </li>
                    ))}
                  </ul>

                  <button
                    data-testid={`group-trip-${trip.id}-reserve`}
                    onClick={() => reserve(trip.title)}
                    className="mt-7 flex w-full items-center justify-center gap-3 rounded-full bg-[#168A91] py-3.5 font-accent text-[11px] uppercase tracking-[0.2em] text-[#F7F0E3] transition-colors duration-300 hover:bg-[#116D73]"
                  >
                    {trip.soldOut ? "Join the Waitlist" : "Request More Information"}
                    <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
